import { buf2hex, concatTypedArrays, hex2buf } from "./util";
import { SFDP, ENTER_4BYTE, EXIT_4BYTE } from "./SFDP";
import { RDID } from "./RDID";

// Dedicated 4-byte address opcodes for the plain 3-byte ones. These always
// take a 4-byte address, independent of the flash's addressing mode.
const FOUR_BYTE_OPCODES = {
    0x03: 0x13, // Read
    0x0B: 0x0C, // Fast Read
    0x02: 0x12, // Page Program
    0x20: 0x21, // 4KB Sector Erase
    0x52: 0x5C, // 32KB Block Erase
    0xD8: 0xDC, // 64KB Block Erase
};

const ADDRESSING_METHODS = ['3-byte', '4-byte opcodes', 'EN4B', '4-byte native'];

// Largest SFDP area we're willing to read when parameter tables point
// beyond the initial 256 bytes.
const SFDP_MAX_LENGTH = 4096;

class Flashdumper {
    constructor(adapter) {
        this.adapter = adapter;
        this.detected = false
        this.manualCapacity = null;
        this.addressingOverride = null;
    }

    async identify() {
        this.detected = false;
        this.rdid = null;
        this.sfdp = null;
        // A fresh discover should override any previous manual size.
        this.manualCapacity = null;
        console.log("Identification started")

        // Primary detection: SFDP
        console.log("- Reading SFDP")
        try {
            this.sfdp = await this.readSFDPPure();
        } catch (e) {
            console.log("SFDP read failed:", e);
            this.sfdp = null;
        }
        console.log("- SFDP:", this.sfdp)

        // Fallback / supplementary: RDID (provides vendor info and
        // a capacity estimate if SFDP is missing).
        console.log("- Reading RDID")
        try {
            this.rdid = await this.readRDID();
        } catch (e) {
            console.log("RDID read failed:", e);
            this.rdid = null;
        }
        console.log("- RDID:", this.rdid)

        if (this.rdid || this.sfdp) {
            this.detected = true;
        }
    }

    setManualCapacity(bytes) {
        this.manualCapacity = bytes;
        // Let the UI treat a user-specified size as a successful detection.
        this.detected = true;
    }

    clearManualCapacity() {
        this.manualCapacity = null;
    }

    getCapacity() {
        // Manual override wins (explicit user intent), then SFDP, then
        // the capacity byte from RDID as a last resort.
        if (this.manualCapacity) {
            return this.manualCapacity;
        }
        if (this.sfdp && this.sfdp.densityBytes) {
            return this.sfdp.densityBytes;
        }
        if (this.rdid && this.rdid.capacityBytes) {
            return this.rdid.capacityBytes;
        }
        throw("No capacity available. Please specify a manual size.");
    }

    getCapacitySource() {
        if (this.manualCapacity) return 'manual';
        if (this.sfdp && this.sfdp.densityBytes) return 'SFDP';
        if (this.rdid && this.rdid.capacityBytes) return 'RDID';
        return null;
    }

    hasCapacity() {
        return this.getCapacitySource() !== null;
    }

    getAddressBytes() {
        // SFDP's address-bytes field only describes the power-on mode:
        // most 256 Mbit parts report "3- or 4-byte, default 3" and some
        // older ones claim "3-byte only". Anything larger than 16 MiB
        // needs 4 address bytes, whatever SFDP says.
        if (this.sfdp && this.sfdp.addressBytesRaw === 0b10) {
            return 4;
        }
        if (this.hasCapacity() && this.getCapacity() > 0x1000000) {
            return 4;
        }
        return 3;
    }

    setAddressingOverride(method) {
        // null = automatic
        if (method !== null && !ADDRESSING_METHODS.includes(method)) {
            throw (`Unknown addressing method: ${method}`);
        }
        this.addressingOverride = method;
    }

    getAddressing() {
        // Decide how addresses are sent to the flash. Methods, in order of
        // preference for > 16 MiB flashes:
        // - '4-byte opcodes': 13h/12h/21h... always take 4 address bytes
        //   and leave the flash's addressing mode untouched. Every
        //   transaction stands alone, so a bus disturbance or a reset by
        //   the target can't desync us, and the target never finds its
        //   flash left in 4-byte mode.
        // - 'EN4B': switch the flash into 4-byte mode (B7h) for the
        //   duration of an operation, and back (E9h) afterwards. Only used
        //   when SFDP says dedicated 4-byte opcodes are not available.
        // - '4-byte native': the flash is always in 4-byte mode, plain
        //   opcodes take 4 address bytes.
        const bfpt = this.sfdp?.bfpt;
        const table = this.sfdp?.fourByteAddressTable;
        const enter = bfpt?.enter4ByteMethods ?? 0;

        let method;
        let reason = null;
        if (this.addressingOverride) {
            method = this.addressingOverride;
            reason = 'manual override';
        } else if (this.getAddressBytes() === 3) {
            method = '3-byte';
        } else if (bfpt?.addressBytesRaw === 0b10 || (enter & ENTER_4BYTE.ALWAYS_4BYTE)) {
            method = '4-byte native';
            reason = 'SFDP: flash only supports 4-byte addressing';
        } else if (table?.supportsRead && table?.supportsPageProgram) {
            method = '4-byte opcodes';
            reason = 'SFDP 4-byte address instruction table';
        } else if (enter & ENTER_4BYTE.DEDICATED_OPCODES) {
            method = '4-byte opcodes';
            reason = 'SFDP: dedicated 4-byte instruction set';
        } else if (enter & (ENTER_4BYTE.EN4B | ENTER_4BYTE.WREN_EN4B)) {
            method = 'EN4B';
            reason = 'SFDP: no dedicated 4-byte opcodes, entering 4-byte mode with B7h';
        } else {
            method = '4-byte opcodes';
            reason = 'no SFDP information on 4-byte addressing, assuming dedicated 4-byte opcodes (supported by most > 16 MiB flashes)';
        }

        const addressing = {
            method,
            reason,
            addressBytes: method === '3-byte' ? 3 : 4,
            read: 0x03,
            program: 0x02,
            erase4k: 0x20,
        };

        if (method === '4-byte opcodes') {
            addressing.read = FOUR_BYTE_OPCODES[0x03];
            addressing.program = FOUR_BYTE_OPCODES[0x02];
            addressing.erase4k = FOUR_BYTE_OPCODES[0x20];
            const erase4kType = this.sfdp?.get4KBEraseType();
            if (table && erase4kType && table.getEraseOpcode(erase4kType.typeNumber) !== null) {
                addressing.erase4k = table.getEraseOpcode(erase4kType.typeNumber);
            }
        }
        return addressing;
    }

    async withAddressing(addressing, operation) {
        // Runs operation() with the flash in the addressing mode the
        // method requires. For EN4B the flash is always put back into
        // 3-byte mode afterwards, including on cancel or error, so the
        // target finds it the way it expects.
        if (addressing.method !== 'EN4B') {
            return await operation();
        }
        const bfpt = this.sfdp?.bfpt;
        const enter = bfpt?.enter4ByteMethods ?? 0;
        const exit = bfpt?.exit4ByteMethods ?? 0;

        if ((enter & ENTER_4BYTE.WREN_EN4B) && !(enter & ENTER_4BYTE.EN4B)) {
            await this.write_enable()
        }
        await this.adapter.spiTransfer(new Uint8Array([0xB7]), 0)
        try {
            return await operation();
        } finally {
            // A cancelled erase/program may still be running, and most
            // flashes ignore E9h while busy.
            await this.waitBusy()
            if ((exit & EXIT_4BYTE.WREN_EX4B) && !(exit & EXIT_4BYTE.EX4B)) {
                await this.write_enable()
                await this.adapter.spiTransfer(new Uint8Array([0xE9]), 0)
                await this.write_disable()
            } else {
                await this.adapter.spiTransfer(new Uint8Array([0xE9]), 0)
            }
        }
    }

    addressCommand(opcode, address, addressBytes, payload = null) {
        const command = new Uint8Array(1 + addressBytes + (payload ? payload.length : 0));
        command[0] = opcode;
        for (let i = 0; i < addressBytes; i++) {
            command[1 + i] = (address >>> (8 * (addressBytes - 1 - i))) & 0xFF;
        }
        if (payload) {
            command.set(payload, 1 + addressBytes);
        }
        return command;
    }

    async readId() {
        const command = new Uint8Array([0x9f])
        const result = await this.adapter.spiTransfer(command, 3);
        // console.log(result);
        let dataview = new DataView(result.data.buffer);
        let id1 = dataview.getUint8(1)
        let id2 = dataview.getUint16(2, 0)
        // 13 - 01 00 00 02 00 00 05 
        return ({
            "id1": id1,
            "id2": id2
        })
    }

    async readMID() {
        let command = new Uint8Array([0x90, 0, 0, 0]);
        const result = await this.adapter.spiTransfer(command, 3);
        return result;
    }

    async readRDID() {
        let command = new Uint8Array([0x9F,]);
        const result = await this.adapter.spiTransfer(command, 3);
        const data = result.subarray(1);
        if((data[0] === 0x0) || (data[1] === 0x0) || (data[2] === 0x0)) {
            console.log("RDID is null");
            return null;
        }
        if((data[0] === 0xff) || (data[1] === 0xff) || (data[2] === 0xff)) {
            console.log("RDID is FF");
            return null;
        }
        return new RDID(data);
    }

    async readSFDPRaw(address, length) {
        // Read SFDP command: 5Ah, 3 address bytes, 1 dummy byte. Read in
        // 256 byte chunks to stay within adapter transfer sizes.
        const data = new Uint8Array(length)
        for (let offset = 0; offset < length; offset += 256) {
            const chunk = Math.min(256, length - offset)
            const command = this.addressCommand(0x5a, address + offset, 3, new Uint8Array(1))
            const result_full = await this.adapter.spiTransfer(command, chunk);
            data.set(result_full.subarray(command.length), offset)
        }
        return data
    }

    async parseSFDP(length) {
        let sfdp = new SFDP(await this.readSFDPRaw(0, length))
        // Parameter tables (e.g. the 4-byte address instruction table)
        // can live beyond the initial read.
        const required = Math.min(sfdp.requiredLength, SFDP_MAX_LENGTH)
        if (required > length) {
            sfdp = new SFDP(await this.readSFDPRaw(0, required))
        }
        return sfdp
    }

    async readSFDPPure(length = 256) {
        await this.winbond_reset()
        try {
            return await this.parseSFDP(length);
        } catch {
            return null;
        }
    }

    async readSFDP(length = 256) {
        await this.winbond_reset()
        const sfdp = await this.parseSFDP(length)
        // Store SFDP as we need it during read to check number
        // of address-bytes etcpp
        this.sfdp = sfdp;
        return sfdp;
    }

    async readStatus(register_number) {
        let status_operation = 0x05;
        if (register_number == 1) {
            // i don't remember why this is :D
            status_operation = 0x35;
        }

        const command = new Uint8Array([status_operation])
        const result = await this.adapter.spiTransfer(command, 3);

        let dataview = new DataView(result);
    }


    async read(blocksize, size, start = 0, progress_callback = undefined) {
        const addressing = this.getAddressing()
        const read_data = new Uint8Array(size)

        var offset = 0;

        await this.withAddressing(addressing, async () => {
            while (offset < size) {
                const left_over = size - offset;
                var read_size = blocksize;
                if (left_over < blocksize) {
                    read_size = left_over;
                }
                const command = this.addressCommand(addressing.read, start + offset, addressing.addressBytes)
                const transfer = await this.adapter.spiTransfer(command, read_size)
                read_data.set(transfer.subarray(command.length), offset)
                if(progress_callback(offset/size, offset, size)) {
                    console.log("Cancel!")
                    break
                }
                offset += read_size;
            }
        });
        return read_data.slice(0, offset)
    }




    // erase_block() {
    //     if(this.offset === this.size) {
    //         this.resolve()
    //         return
    //     }

    //     // Offset 1 so we don't overwrite the 03 (Read command)
    //     this.command_view.setUint32(1, this.offset, 0)
    //     // Yes I'm a great programmer ;D
    //     this.command[1] = this.command[2]
    //     this.command[2] = this.command[3]
    //     this.command[3] = this.command[4]
    //     this.progress = (this.offset/this.size) * 100
    //     this.onUpdate(this)
    //     this.serprog.spiOp(0x13, new Uint8Array([0x06]), 0).then((data) => {
    //         this.serprog.spiOp(0x13, this.command.subarray(0, 4), 0).then((data) => {
    //             if(this.cancelled) {
    //                 this.resolve(null)
    //                 return
    //             }
    //             this.offset += this.blocksize
    //             this.serprog.waitBusy().then(() => {
    //                 this.erase_block()
    //             })

    //         })
    //     })
    // }

    async winbond_reset() {
        await this.adapter.spiTransfer(new Uint8Array([0x66]), 0);
        await this.adapter.spiTransfer(new Uint8Array([0x99]), 0);
    }

    async status(register_number = 0) {
        let status_op = 0x05;
        if (register_number == 1) {
            status_op = 0x35;
        }

        const transfer = await this.adapter.spiTransfer(new Uint8Array([status_op]), 1)
        return transfer.subarray(1)
    }


    async isBusy() {
        const status = await this.status(0)
        if (status[0] & 0x1) {
            return true;
        }
        return false;
    }

    async waitBusy() {
        while (await this.isBusy()) { };
    }

    async write_enable() {
        // This function just sends the write enable command, it does not control
        // the WP etcpp lines on the programmer!
        const transfer = await this.adapter.spiTransfer(new Uint8Array([0x06]), 0)
    }

    async write_disable() {
        await this.adapter.spiTransfer(new Uint8Array([0x04]), 0)
    }

    async erase(start, size, progress_callback) {
        const addressing = this.getAddressing()
        // TODO get this from SFDP
        const blocksize = 4 * 1024

        if (start % blocksize != 0) {
            throw ("ERASE Offset needs to be aligned to blocksize. It's NOT in blocks.");
        }

        if (start % blocksize != 0) {
            throw ("ERASE Size needs to be aligned to blocksize. It's NOT in blocks.");
        }

        var offset = 0;

        await this.withAddressing(addressing, async () => {
            while (offset < size) {
                const command = this.addressCommand(addressing.erase4k, start + offset, addressing.addressBytes)
                await this.write_enable()
                const transfer = await this.adapter.spiTransfer(command, 0)

                if(progress_callback(offset/size, offset, size)) {
                    console.log("Cancel!")
                    break
                }
                await this.adapter.selectChip()
                offset += blocksize
                await this.waitBusy()
            }
        });
    }



    async write(offset, data, progress_callback) {
        const addressing = this.getAddressing()
        // TODO get this from SFDP
        const blocksize = 256

        const size = data.length;

        if (offset % blocksize != 0) {
            throw ("Offset needs to be aligned to blocksize. It's NOT in blocks.");
        }

        if (size % blocksize != 0) {
            throw ("Data size currently needs to be aligned to blocksize.");
        }

        // var offset = 0;
        var written = 0;

        await this.withAddressing(addressing, async () => {
            while (written < size) {
                const writeBuffer = this.addressCommand(addressing.program, offset + written,
                    addressing.addressBytes, data.subarray(written, written + blocksize))
                await this.write_enable()
                const transfer = await this.adapter.spiTransfer(writeBuffer, 0)
                if(progress_callback) {
                    if(progress_callback(written/size, written, size)) {
                        console.log("Cancel!")
                        break
                    }
                }
                await this.adapter.selectChip()
                written += blocksize
                await this.waitBusy()
            }
        });
    }


}

export { Flashdumper, ADDRESSING_METHODS };